import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  Table,
  Button,
  Form,
  Modal,
  Spinner,
  Alert,
  Pagination,
  InputGroup,
  Card,
  Badge,
  Container,
  Row,
  Col,
  Nav,
  Tab,
} from "react-bootstrap";
import AuthContext from "../contexts/AuthContext";
import api from "../api";

const PendingQuestions = () => {
  const [pendingData, setPendingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [processingBatch, setProcessingBatch] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [activeTab, setActiveTab] = useState("question");
  const [previewKey, setPreviewKey] = useState(0);

  // New state for subject filtering
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [subjectSelected, setSubjectSelected] = useState(false);

  const questionEditorRef = useRef(null);
  const markschemeEditorRef = useRef(null);
  const questionPreviewRef = useRef(null);
  const markschemePreviewRef = useRef(null);
  const { auth } = useContext(AuthContext);

  // Check if user is admin
  const isAdmin = auth.user?.is_staff || auth.user?.is_superuser;

  // Fetch subjects when component mounts
  useEffect(() => {
    if (isAdmin) {
      fetchPendingSubjects();
    }
  }, [isAdmin]);

  // Fetch pending data when subject changes or page changes
  // Only fetch data if a subject is selected
  useEffect(() => {
    if (isAdmin && selectedSubject) {
      fetchPendingData();
      setSubjectSelected(true);
    }
  }, [isAdmin, currentPage, selectedSubject]);

  // Fetch subjects that exist in pending questions
  const fetchPendingSubjects = async () => {
    try {
      setLoadingSubjects(true);
      setSubjects([]); // Clear existing subjects before loading
      setError(""); // Clear any previous errors

      try {
        // Get unique subjects directly from the API endpoint for subjects in pending questions
        const subjectsResponse = await api.get(
          "/api/csv-data/pending-subjects/"
        );

        if (subjectsResponse.data && Array.isArray(subjectsResponse.data)) {
          // Map the response to the expected format
          const uniqueSubjects = subjectsResponse.data.map((item) => ({
            name: item.subject,
            count: item.count,
            isReal: true,
          }));

          // Sort subjects alphabetically
          uniqueSubjects.sort((a, b) => a.name.localeCompare(b.name));

          setSubjects(uniqueSubjects);
          console.log("Subjects loaded:", uniqueSubjects);
        } else {
          // Fallback to using the pending API
          console.log(
            "API didn't return expected format, using fallback method"
          );

          try {
            // Get pending questions using pagination with a large page size
            const fallbackResponse = await api.get(
              "/api/csv-data/pending/?page_size=999"
            );

            const pendingQuestions = fallbackResponse.data?.results || [];
            console.log(`Got ${pendingQuestions.length} pending questions`);

            // Extract unique subjects and count questions for each
            const fallbackSubjectCounts = {};
            pendingQuestions.forEach((item) => {
              if (item.subject) {
                fallbackSubjectCounts[item.subject] =
                  (fallbackSubjectCounts[item.subject] || 0) + 1;
              }
            });

            // Create array for subjects
            const fallbackSubjectsList = Object.keys(fallbackSubjectCounts)
              .filter((subject) => subject && subject.trim() !== "")
              .map((subject) => ({
                name: subject,
                count: fallbackSubjectCounts[subject],
                isReal: true,
              }))
              .sort((a, b) => a.name.localeCompare(b.name));

            if (fallbackSubjectsList.length === 0) {
              console.log("No subjects found, showing empty state");
              setSubjects([
                {
                  name: "No pending questions found",
                  count: 0,
                  isReal: true,
                  disabled: true,
                },
              ]);
            } else {
              setSubjects(fallbackSubjectsList);
              console.log(
                "Subjects loaded (fallback method):",
                fallbackSubjectsList
              );
            }
          } catch (fallbackErr) {
            console.error("Fallback method failed:", fallbackErr);
            setError("Failed to load subjects. Please try again later.");
            setSubjects([
              {
                name: "Error loading subjects",
                count: 0,
                isReal: true,
                disabled: true,
              },
            ]);
          }
        }
      } catch (err) {
        console.error("Error loading subjects:", err);
        setError(
          "Failed to load subjects. Please refresh the page or try again later."
        );
      }
    } catch (err) {
      console.error("Error in subjects loading:", err);
      setError("Failed to load subjects. Please refresh the page.");
    } finally {
      setLoadingSubjects(false);
    }
  };

  const fetchPendingData = async () => {
    // Don't fetch data if no subject is selected
    if (!selectedSubject) {
      setPendingData([]);
      setTotalItems(0);
      setTotalPages(0);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Add subject filter
      params.append("subject", selectedSubject);

      // Add search term if provided, specifically for reference_code field
      if (searchTerm) {
        params.append("reference_code", searchTerm.trim());
      }

      params.append("page", currentPage);

      const response = await api.get(
        `/api/csv-data/pending/?${params.toString()}`
      );

      if (
        response.data &&
        response.data.results &&
        Array.isArray(response.data.results)
      ) {
        setPendingData(response.data.results);
        setTotalItems(response.data.count);
        setTotalPages(Math.ceil(response.data.count / 10));
      } else {
        console.error("Unexpected API response format:", response.data);
        setPendingData([]);
        setError("Received unexpected data format from the server");
      }
    } catch (err) {
      console.error("Error fetching pending data:", err);
      setError("Failed to load pending questions. Please try again later.");
      setPendingData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle subject filter change
  const handleSubjectChange = (e) => {
    const subject = e.target.value;

    // Check if this is a disabled option (error state or empty state)
    const selectedSubjectObj = subjects.find((s) => s.name === subject);
    if (selectedSubjectObj && selectedSubjectObj.disabled) {
      return; // Don't allow selection of disabled options
    }

    setSelectedSubject(subject);
    setCurrentPage(1); // Reset to first page when changing subject

    if (!subject) {
      setSubjectSelected(false);
      setPendingData([]);
      setTotalItems(0);
      setTotalPages(0);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();

    // Only search if a subject is selected
    if (selectedSubject) {
      setCurrentPage(1); // Reset to first page when searching
      fetchPendingData();
    } else {
      setError("Please select a subject before searching");
      setTimeout(() => setError(""), 3000);
    }
  };

  // Handle clearing all filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedSubject("");
    setSubjectSelected(false);
    setCurrentPage(1);
    setPendingData([]);
    setTotalItems(0);
    setTotalPages(0);
  };

  const handleViewDetails = (record) => {
    setCurrentRecord(record);
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this pending question?")
    ) {
      try {
        await api.delete(`/api/csv-data/${id}/`);
        // Refresh data after deletion
        fetchPendingData();
        // Refresh subjects as well, in case we deleted the last question with a certain subject
        fetchPendingSubjects();
        setSuccessMessage("Question deleted successfully");
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } catch (err) {
        console.error("Error deleting record:", err);
        setError("Failed to delete pending question. Please try again.");
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/api/csv-data/${id}/approve/`);
      // Refresh data after approval
      fetchPendingData();
      // Refresh subjects as well, in case we approved the last question with a certain subject
      fetchPendingSubjects();
      setSuccessMessage("Question approved successfully");
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error approving record:", err);
      setError("Failed to approve pending question. Please try again.");
    }
  };

  // Function to synchronize heights
  const syncEditorPreviewHeight = useCallback(() => {
    if (
      activeTab === "question" &&
      questionEditorRef.current &&
      questionPreviewRef.current
    ) {
      questionPreviewRef.current.style.height = `${questionEditorRef.current.offsetHeight}px`;
    } else if (
      activeTab === "markscheme" &&
      markschemeEditorRef.current &&
      markschemePreviewRef.current
    ) {
      markschemePreviewRef.current.style.height = `${markschemeEditorRef.current.offsetHeight}px`;
    }
  }, [activeTab]);

  // Add resize observer to monitor editor size changes
  useEffect(() => {
    const activeEditor =
      activeTab === "question"
        ? questionEditorRef.current
        : markschemeEditorRef.current;

    if (activeEditor) {
      const resizeObserver = new ResizeObserver(() => {
        syncEditorPreviewHeight();
      });

      resizeObserver.observe(activeEditor);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [activeTab, syncEditorPreviewHeight]);

  // Sync heights on tab change
  useEffect(() => {
    syncEditorPreviewHeight();
  }, [activeTab, syncEditorPreviewHeight]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRecord({ ...currentRecord, [name]: value });

    // Update preview with debounce
    if (name === "question_body" || name === "markscheme_body") {
      updatePreviewWithDebounce();
    }
  };

  // Debounce function for preview updates
  const updatePreviewWithDebounce = useCallback(
    (() => {
      let debounceTimer;
      return () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          setPreviewKey((prevKey) => prevKey + 1);
        }, 300);
      };
    })(),
    []
  );

  const handleSubmit = async () => {
    try {
      const response = await api.put(
        `/api/csv-data/${currentRecord.id}/`,
        currentRecord
      );
      // Update the specific item in the list if it exists
      setPendingData(
        pendingData.map((item) =>
          item.id === currentRecord.id ? response.data : item
        )
      );
      setShowEditModal(false);
      setSuccessMessage("Question updated successfully");
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error updating record:", err);
      setError("Failed to update pending question. Please try again.");
    }
  };

  const handleCheckboxChange = (id) => {
    if (selectedRecords.includes(id)) {
      setSelectedRecords(selectedRecords.filter((recordId) => recordId !== id));
    } else {
      setSelectedRecords([...selectedRecords, id]);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRecords(pendingData.map((item) => item.id));
    } else {
      setSelectedRecords([]);
    }
  };

  const handleBatchApprove = async () => {
    if (selectedRecords.length === 0) {
      setError("No records selected for approval");
      return;
    }

    setProcessingBatch(true);
    try {
      const response = await api.post("/api/csv-data/approve_records/", {
        record_ids: selectedRecords,
      });

      // Refresh data after batch approval
      fetchPendingData();
      setSelectedRecords([]);
      setSuccessMessage(
        `Successfully approved ${response.data.approved_count} questions`
      );

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error batch approving records:", err);
      setError("Failed to approve the selected questions. Please try again.");
    } finally {
      setProcessingBatch(false);
    }
  };

  // Helper function to create a preview of HTML content
  const createHTMLPreview = (htmlContent, maxLength = 100) => {
    if (!htmlContent) return "-";

    // Create a temporary DOM element to strip HTML tags for the preview
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    // Return truncated text for preview
    return textContent.length > maxLength
      ? `${textContent.substring(0, maxLength)}...`
      : textContent;
  };

  // Function to safely render HTML content
  const renderHTML = (htmlContent) => {
    return { __html: htmlContent || "" };
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Add custom CSS styles
  const editorStyles = {
    splitContainer: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      gap: "15px",
    },
    editorPane: {
      flex: 1,
      fontFamily: "monospace",
      fontSize: "14px",
    },
    previewPane: {
      flex: 1,
      border: "1px solid #dee2e6",
      borderRadius: "0.25rem",
      padding: "15px",
      height: "300px",
      overflowY: "auto",
      backgroundColor: "#f8f9fa",
    },
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "14px",
      lineHeight: "1.5",
    },
  };

  // If not admin, redirect or show access denied
  if (!isAdmin) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>You do not have permission to view this page.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <div>
      <h2 className="mb-4">Pending Questions Admin Panel</h2>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          variant="success"
          onClose={() => setSuccessMessage("")}
          dismissible
        >
          {successMessage}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              <strong>Select a Subject to View Questions</strong>
            </Form.Label>
            <Form.Select
              value={selectedSubject}
              onChange={handleSubjectChange}
              disabled={loadingSubjects}
              className="form-control-lg"
            >
              <option value="">
                {loadingSubjects
                  ? "Loading subjects..."
                  : "-- Select a Subject --"}
              </option>
              {subjects.map((subject, index) => (
                <option
                  key={index}
                  value={subject.name}
                  disabled={subject.disabled}
                >
                  {subject.name}{" "}
                  {subject.count > 0
                    ? `(${subject.count} question${
                        subject.count !== 1 ? "s" : ""
                      })`
                    : ""}
                </option>
              ))}
            </Form.Select>
            {loadingSubjects && (
              <div className="text-center mt-2">
                <Spinner animation="border" size="sm" role="status">
                  <span className="visually-hidden">Loading subjects...</span>
                </Spinner>
                <span className="ms-2">Loading subjects...</span>
              </div>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Label>Search by Reference Code</Form.Label>
          <Form onSubmit={handleSearch}>
            <InputGroup>
              <Form.Control
                placeholder="Enter exact reference code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!selectedSubject}
              />
              <Button
                variant="primary"
                type="submit"
                disabled={!selectedSubject}
              >
                Search
              </Button>
              <Button variant="outline-secondary" onClick={handleClearFilters}>
                Clear All
              </Button>
            </InputGroup>
          </Form>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col className="d-flex justify-content-between">
          <div>
            {totalItems > 0 && (
              <Badge bg="info" className="p-2">
                {`Showing ${pendingData.length} pending questions for ${selectedSubject}`}
              </Badge>
            )}
          </div>
          <Button
            variant="success"
            disabled={selectedRecords.length === 0 || processingBatch}
            onClick={handleBatchApprove}
          >
            {processingBatch
              ? "Processing..."
              : `Approve Selected (${selectedRecords.length})`}
          </Button>
        </Col>
      </Row>

      {!selectedSubject ? (
        <Card className="text-center p-5 my-5">
          <Card.Body>
            <Card.Title>No Subject Selected</Card.Title>
            <Card.Text>
              Please select a subject from the dropdown menu above to view
              pending questions.
            </Card.Text>
          </Card.Body>
        </Card>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">
            Loading pending questions for {selectedSubject}...
          </p>
        </div>
      ) : (
        <>
          {pendingData.length === 0 ? (
            <Card className="text-center p-4">
              <Card.Body>
                <Card.Title>No Pending Questions</Card.Title>
                <Card.Text>
                  {`No pending questions found for subject "${selectedSubject}"${
                    searchTerm ? ` with reference code "${searchTerm}"` : ""
                  }`}
                </Card.Text>
              </Card.Body>
            </Card>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th width="5%">
                      <Form.Check
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={
                          selectedRecords.length === pendingData.length &&
                          pendingData.length > 0
                        }
                      />
                    </th>
                    <th width="10%">Subject</th>
                    <th width="35%">Question</th>
                    <th width="35%">Mark Scheme</th>
                    <th width="15%">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingData.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedRecords.includes(item.id)}
                          onChange={() => handleCheckboxChange(item.id)}
                        />
                      </td>
                      <td>{item.subject || "-"}</td>
                      <td>
                        <div className="question-body-preview">
                          {createHTMLPreview(item.question_body, 150)}
                          {item.question_body &&
                            item.question_body.length > 150 && (
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => handleViewDetails(item)}
                                className="p-0 ms-1"
                              >
                                View Full Question
                              </Button>
                            )}
                        </div>
                      </td>
                      <td>
                        <div className="markscheme-body-preview">
                          {createHTMLPreview(item.markscheme_body, 150)}
                          {item.markscheme_body &&
                            item.markscheme_body.length > 150 && (
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => handleViewDetails(item)}
                                className="p-0 ms-1"
                              >
                                View Full Mark Scheme
                              </Button>
                            )}
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="success"
                          size="sm"
                          className="me-1 mb-1"
                          onClick={() => handleApprove(item.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          className="me-1 mb-1"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="justify-content-center mt-4">
              <Pagination.First
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {[...Array(totalPages).keys()]
                .slice(
                  Math.max(0, currentPage - 3),
                  Math.min(totalPages, currentPage + 2)
                )
                .map((number) => (
                  <Pagination.Item
                    key={number + 1}
                    active={number + 1 === currentPage}
                    onClick={() => paginate(number + 1)}
                  >
                    {number + 1}
                  </Pagination.Item>
                ))}

              <Pagination.Next
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          )}
        </>
      )}

      {/* View Details Modal with HTML rendering */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Pending Question Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="text-primary">{currentRecord.title || "Untitled"}</h5>
          <p>
            <Badge bg="secondary" className="me-1">
              Level: {currentRecord.level || "Not specified"}
            </Badge>
            <Badge bg="info" className="me-1">
              Subject: {currentRecord.subject || "Not specified"}
            </Badge>
            <Badge bg="dark">
              Reference: {currentRecord.reference_code || "Not specified"}
            </Badge>
          </p>

          <h6 className="mt-4">Question:</h6>
          <div
            className="border p-3 mb-3 bg-light html-content-view"
            dangerouslySetInnerHTML={renderHTML(currentRecord.question_body)}
          ></div>

          <h6>Mark Scheme:</h6>
          <div
            className="border p-3 bg-light html-content-view"
            dangerouslySetInnerHTML={renderHTML(currentRecord.markscheme_body)}
          ></div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => {
              handleApprove(currentRecord.id);
              setShowModal(false);
            }}
          >
            Approve
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowModal(false);
              setShowEditModal(true);
            }}
          >
            Edit
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="xl"
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Pending Question</Modal.Title>
        </Modal.Header>
        <Modal.Body className="editor-preview-container">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={currentRecord.title || ""}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Level</Form.Label>
                  <Form.Control
                    type="text"
                    name="level"
                    value={currentRecord.level || ""}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    type="text"
                    name="subject"
                    value={currentRecord.subject || ""}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Reference Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="reference_code"
                    value={currentRecord.reference_code || ""}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Tab.Container
              id="editor-tabs"
              defaultActiveKey="question"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
            >
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="question">Question Content</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="markscheme">Mark Scheme Content</Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey="question">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Question (HTML Format)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={12}
                          name="question_body"
                          value={currentRecord.question_body || ""}
                          onChange={handleInputChange}
                          style={editorStyles.monospaceFont}
                          className="resizable-editor"
                          ref={questionEditorRef}
                          onKeyUp={() => syncEditorPreviewHeight()}
                        />
                        <Form.Text className="text-muted">
                          You can use HTML tags to format the content.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Live Preview</Form.Label>
                        <div
                          className="html-content-view"
                          style={editorStyles.previewPane}
                          key={`question-preview-${previewKey}`}
                          dangerouslySetInnerHTML={renderHTML(
                            currentRecord.question_body
                          )}
                          ref={questionPreviewRef}
                        ></div>
                        <Form.Text className="text-muted">
                          This shows exactly how the question will appear to
                          students.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab.Pane>
                <Tab.Pane eventKey="markscheme">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mark Scheme (HTML Format)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={12}
                          name="markscheme_body"
                          value={currentRecord.markscheme_body || ""}
                          onChange={handleInputChange}
                          style={editorStyles.monospaceFont}
                          className="resizable-editor"
                          ref={markschemeEditorRef}
                          onKeyUp={() => syncEditorPreviewHeight()}
                        />
                        <Form.Text className="text-muted">
                          You can use HTML tags to format the content.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Live Preview</Form.Label>
                        <div
                          className="html-content-view"
                          style={editorStyles.previewPane}
                          key={`markscheme-preview-${previewKey}`}
                          dangerouslySetInnerHTML={renderHTML(
                            currentRecord.markscheme_body
                          )}
                          ref={markschemePreviewRef}
                        ></div>
                        <Form.Text className="text-muted">
                          This shows exactly how the mark scheme will appear to
                          students.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PendingQuestions;
